import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Container from '@mui/material/Container/Container';
import Stack from '@mui/material/Stack/Stack';
import Box from '@mui/material/Box/Box';
import Typography from '@mui/material/Typography/Typography';
import Alert from '@mui/material/Alert/Alert';
import { alpha } from '@mui/material/styles';
import { Loading } from '../components/Loading';
import { PlanCards } from '../components/Billing/PlanCards';
import { getPricing, Pricing as PricingData } from '../services/billing.service';
import { LOOK } from '../theme';

// from docs/behaviour/pricing-and-plans.md ("Questions customers ask"); keep the two in step
const QUESTIONS = [
    { q: 'Do we pay per person?', a: 'No. One payment per event, whatever its size.' },
    { q: 'Can we set up our branding before we pay?', a: 'Yes. Set it up and preview it; it goes live when you upgrade.' },
    { q: 'We have several events a year.', a: 'Each event is upgraded separately, when you want it to look like your company.' },
    { q: 'Can we try it first?', a: 'Every event is free to run end to end. Only branding needs Pro.' }
];

/** The public pricing page: per-event pricing, Free next to Pro event, and the questions customers ask. */
export const Pricing = () => {
    const [pricing, setPricing] = useState<PricingData | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        getPricing().then(setPricing, () => setError('Could not load pricing'));
    }, []);

    return (
        <Container>
            <Stack spacing={{ xs: 5, md: 8 }} sx={{ maxWidth: 1080, mx: 'auto', py: { md: 2 } }}>
                <Stack spacing={2.5} sx={{ maxWidth: 820 }}>
                    <Typography variant="overline" color="text.secondary">Pricing</Typography>
                    <Typography variant="h1" sx={{ fontSize: { xs: '3rem', md: '5rem' }, lineHeight: 0.95 }}>
                        Free to run. Pay per event to make it yours.
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '1.15rem', md: '1.35rem' }, maxWidth: 620 }} color="text.secondary">
                        No subscription and no seats. Upgrade a hackathon only when you want it to look like your company.
                    </Typography>
                </Stack>

                {error && <Alert severity="error">{error}</Alert>}
                {!pricing && !error && <Loading />}
                {pricing && (
                    <PlanCards pricing={pricing} proAction={
                        <Typography variant="body2" color="text.secondary">
                            Upgrade any hackathon you organize from its <b>Organize</b> page, before or during the event.
                        </Typography>
                    } />
                )}

                <Box sx={{ display: 'grid', gap: { xs: 3, md: 6 }, gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' } }}>
                    <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' } }}>Questions customers ask</Typography>
                    <Box>
                        {QUESTIONS.map(({ q, a }) => (
                            <Box key={q} sx={{ py: 2.5, borderTop: `1px solid ${alpha(LOOK.ink, 0.15)}` }}>
                                <Typography variant="h4" component="h3" sx={{ mb: 0.75 }}>{q}</Typography>
                                <Typography color="text.secondary">{a}</Typography>
                            </Box>
                        ))}
                        <Typography color="text.secondary" sx={{ pt: 2.5, borderTop: `1px solid ${alpha(LOOK.ink, 0.15)}` }}>
                            Anything else? See the <Link to="/how/to">how-to guides</Link>.
                        </Typography>
                    </Box>
                </Box>
            </Stack>
        </Container>
    );
}
